// Calibrates the render-time procedural weave to a real thread scale (#124).
//
// The fabric pass (components/pillowFabric.ts) draws the weave as a cosine
// crosshatch in UV space: `cos(uv * uWeaveDensity * TAU)`, so uWeaveDensity is
// the number of thread CYCLES per UV unit. The pet texture is bbox-tight (#121),
// so UV [0,1] is printed across roughly the product's physical size — which lets
// us express the weave in real millimetres instead of a magic number, and makes
// the producer-swatch calibration "set the measured thread pitch", not eyeball a
// density.

export interface ThreadScale {
  // Physical product size (cm) that the pet's bbox-tight UV [0,1] is printed across.
  uvSpanCm: number;
  // One weave cycle (a thread + its valley) every this many millimetres.
  threadPitchMm: number;
}

// Weave cycles per UV unit for the given real thread scale.
export function weaveDensityForThreadPitch({ uvSpanCm, threadPitchMm }: ThreadScale): number {
  if (!(threadPitchMm > 0)) {
    throw new RangeError(`threadPitchMm must be > 0, got ${threadPitchMm}`);
  }
  return (uvSpanCm * 10) / threadPitchMm;
}

// PRODUCTION CALIBRATION — the smooth print fabric (#124).
//
// The product ships at 40×40 / 50×50 / 40×60 cm (STATIC_RECTANGLE_SIZES). The
// weave is a uniform surface texture, so it's calibrated to the mid size — the
// fwidth band-limit fades it at distance anyway, so the ±20% scale drift across
// the size range is well inside "fine threads".
export const PRODUCT_UV_SPAN_CM = 50;

// Operator visual sign-off (2026-06-22): density 750 on the Stage-Lab slider — a
// ~0.67 mm thread, coarser than the pre-swatch 0.3–0.5 mm estimate, because the
// finer weave washed flat under the Stage IBL. Expressed as a thread pitch (not a
// raw density) so the producer-swatch recalibration stays a one-constant edit.
// REPLACE with the measured pitch once the physical swatch arrives.
export const FABRIC_THREAD_PITCH_MM = 2 / 3; // ≈0.67 mm → 750 cycles/UV-unit @ 50 cm

// The shipped weave density — derived, never magic. Feeds DEFAULT_STAGE_CONFIG.
export const PRODUCTION_WEAVE_DENSITY = weaveDensityForThreadPitch({
  uvSpanCm: PRODUCT_UV_SPAN_CM,
  threadPitchMm: FABRIC_THREAD_PITCH_MM,
});
